import { useQuery } from '@tanstack/react-query';

import { fbmApi } from '~/api/fbm';

export const FBM_ITEMS_QUERY_KEY = 'fbmItems';

/**
 * Query key factory for FBM items
 */
export const fbmItemsQueryKeys = {
    all: [FBM_ITEMS_QUERY_KEY],
    byAccount: (fbmAccountName) => [FBM_ITEMS_QUERY_KEY, fbmAccountName],
};

/**
 * Fetches FBM items for a given account.
 * Returns item data including baseUnitsPerCase for each SKU.
 *
 * @param {Object} options
 * @param {string} options.fbmAccountName - The FBM account name to fetch items for
 * @param {boolean} [options.enabled=true] - Whether to enable the query
 * @returns {import('@tanstack/react-query').UseQueryResult<Array<{thirdPartyItemID: string, name: string, baseUnitsPerCase: number}>, Error>}
 */
export function useFbmItemsQuery({ enabled = true, fbmAccountName }) {
    return useQuery({
        enabled: enabled && !!fbmAccountName,
        gcTime: 30 * 60_000, // Keep in cache for 30 minutes
        queryFn: async () => {
            const response = await fbmApi.getItems(fbmAccountName);
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch FBM items');
            }
            return response.data?.rows ?? [];
        },
        queryKey: fbmItemsQueryKeys.byAccount(fbmAccountName),
        staleTime: 15 * 60_000, // Consider fresh for 15 minutes
    });
}
